import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {FormBuilder, FormControl, FormGroup, NgForm, Validators} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {EditRiskModalComponent} from '../../risks/edit-risk-modal/edit-risk-modal.component';
import {CommissionAnnex} from '../../../../shared/interface/commission-annex';
import {CommissionAnnexService} from '../../../../shared/services/commission-annex.service';
import {EditAnnexModalComponent} from '../edit-annex-modal/edit-annex-modal.component';
import {InsuranceCompanyService} from "../../../../shared/services/insurance-company.service";
import {RiskDictionary} from "../../../../shared/interface/risk-dictionary";
import {RiskDictionaryService} from "../../../../shared/services/risk-dictionary.service";
import {CloneAnnexesDialogComponent} from "../../../../shared/components/clone-annexes-dialog/clone-annexes-dialog.component";

@Component({
  selector: 'app-all-annexes-list',
  templateUrl: './all-annexes-list.component.html',
  styleUrls: ['./all-annexes-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AllAnnexesListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;

  initialFilters: any;
  initialized = false;
  items: CommissionAnnex[] = [];
  displayedColumns: string[] = [
    'id', 'partner', 'ofwca', 'insuranceCompany', 'importer',  'internalRisk', 'customerName',
    'policyNumber', 'ratePaid', 'policyTypes', 'activeFrom', 'activeTo', 'archive', 'valid', 'actions'
  ];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  selectedInsuranceCompany: InsuranceCompany;
  expandedElement: CommissionAnnex = null;
  saveExpandedElement: CommissionAnnex = null;
  modify = false;
  editFilter: any;
  annexFilterForm: FormGroup;
  riskDictionaryControl: FormControl;


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionAnnexService: CommissionAnnexService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private insuranceCompanyService: InsuranceCompanyService,
    private riskDictionaryService: RiskDictionaryService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
    private fb: FormBuilder,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionAnnexService.annexConfigurationListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionAnnex>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;

          if (this.initialFilters?.uuid) {
            this.expandElement(this.items[0]);
          }

          if (this.saveExpandedElement) {
            this.items.forEach((commissionAnnex: CommissionAnnex) => {
              if (commissionAnnex.uuid === this.saveExpandedElement.uuid) {
                this.expandedElement = commissionAnnex;
              }
            });
            this.saveExpandedElement = null;
          }
        })
      ).subscribe();
    this.parseFilters({ archive: '0' });
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      uuid: 'string',
      recordId: 'string',
      partner: 'string',
      ofwca: 'string',
      in$insuranceCompany: 'int[]',
      in$importer: 'int[]',
      in$internalRisk: 'int[]',
      riskDictionaryName: 'string',
      archive: 'int',
      valid: 'int',
      overvoltage: 'int',
      gte$activePolicyFrom: 'string',
      lte$activePolicyTo: 'string',
      standard2025: 'int',
      standard2026: 'int',
      customCommission: 'int'
    }, filters);
    if (filters?.in$insuranceCompany) {
      this.insuranceCompanyService.get(filters?.in$insuranceCompany[0])
        .pipe().subscribe((insuranceCompany) => {
          this.selectedInsuranceCompany = insuranceCompany;
      });
    }
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.subscribeQueryParams();
  }

  subscribeQueryParams(): void {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        if (this.initialized) {
          this.parseFilters(routerState.params);
        } else {
          this.parseFilters({...routerState.params, archive: '0'});
          this.initialized = true;
        }
        this.paginator.pageIndex = routerState.params.page;
        this.paginator.pageSize = routerState.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.commissionAnnexService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    return this.initialFilters;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.commissionAnnexService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(ca: CommissionAnnex): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć konfigurację?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commissionAnnexService.deleteConfiguration(ca.uuid)
          .subscribe((response: Uuid) => {
            const dialogRefSecond = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRefSecond.afterClosed().subscribe({
                next: () => {
                  // this.snack.open('Pomyślnie usunięto konfigurację', '', {duration: 2000});
                  this.loadListData();
                },
                error: (error: HttpErrorResponse) => {
                  if (error.status === 400) {
                    this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
                    return;
                  }
                  this.snack.open('Przy usuwaniu wystąpił błąd', error.status.toString(), {duration: 2000});
                }
              });
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy usuwaniu wystąpił błąd', error.status.toString(), {duration: 2000});
          });
      }
    });
  }

  edit(ca: CommissionAnnex, clone): void {
    const dialogRef = this.dialog.open(EditAnnexModalComponent, {
      width: '90vw',
      data: {commissionAnnex: ca, filter: this.editFilter, clone},
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });
  }

  expandElement(commissionAnnex: CommissionAnnex): void {
    this.expandedElement = this.expandedElement === commissionAnnex ? null : commissionAnnex;
    this.modify = false;
  }

  modificationClosed(): void {
    this.saveExpandedElement = this.expandedElement;
    this.loadListData();
  }

  clone(commissionAnnex: CommissionAnnex): void {
    this.store
      .pipe(
        first(),
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        this.editFilter = routerState.params;
      })
    ;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz skopiować konfigurację?'},
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.commissionAnnexService.clone(commissionAnnex.uuid)
          .subscribe((response: Uuid) => {
            this.router.navigate(['/aneksy', {uuid: response.uuid}]);
            this.commissionAnnexService.get(response.uuid)
              .subscribe((newCommissionAnnex: CommissionAnnex) => {
                this.edit(newCommissionAnnex, true);
              });
          });
      }
    });
  }
  cloneAnnexes(commissionAnnex: CommissionAnnex): void {
    const dialogRef = this.dialog.open(CloneAnnexesDialogComponent, {
      width: '500px',
      data: {text: 'Kopiuj wszystkie aneksy dla:', commissionAnnexId: commissionAnnex.uuid},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }
}
